import React, { useEffect, useState } from 'react';
import {
	collection,
	getDocs,
	addDoc,
	updateDoc,
	doc,
	deleteDoc,
} from 'firebase/firestore';
import { db } from '../config/FirebaseConfig';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
	CircularProgress,
	Checkbox,
	FormControlLabel,
	TableSortLabel,
	DialogContentText,
	InputAdornment,
	IconButton,
	Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getAuth, signOut } from 'firebase/auth';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// const CATEGORY_OPTIONS = [
// 	{ value: 'general', label: 'General' },
// 	{ value: 'vip', label: 'VIP' },
// 	{ value: 'family', label: 'Family' },
// ];

const GuestList = () => {
	// ...existing code...
	const [guests, setGuests] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [customToken, setCustomToken] = useState(false);
	const [form, setForm] = useState({
			id: '',
			name: '',
			category: '',
			pax: 1,
			token: 0,
			value: 0,
			table_no: 0,
			is_redeemed: false,
			is_arrived: false,
			is_confirmed: false,
		});
	const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
	const [filterArrived, setFilterArrived] = useState(false);
	const [filterConfirmed, setFilterConfirmed] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [searchName, setSearchName] = useState('');
	const [searchCategory, setSearchCategory] = useState('');
	const [copySuccess, setCopySuccess] = useState('');
	const [showUnconfirmed, setShowUnconfirmed] = useState(false);
	const [showUnarrived, setShowUnarrived] = useState(false);
	const isMobile = useMediaQuery('(max-width:600px)');
	const navigate = useNavigate();

	// Fetch guests from Firestore only after auth
	useEffect(() => {
		const fetchGuests = async () => {
			setLoading(true);
			try {
				const querySnapshot = await getDocs(collection(db, 'guests'));
				const guestsArr = [];
				querySnapshot.forEach((doc) => {
					guestsArr.push({ id: doc.id, ...doc.data() });
				});
				setGuests(guestsArr);
			} catch (err) {
				console.error('Failed to fetch guests', err);
				// avoid uncaught runtime error overlay — show minimal feedback
				// you may want to show a user-facing message here if desired
			} finally {
				setLoading(false);
			}
		};
		fetchGuests();
	}, []);

	// Fetch categories from Firestore
	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const querySnapshot = await getDocs(collection(db, 'categories'));
				const cats = [];
				querySnapshot.forEach((doc) => {
					cats.push({ value: doc.id, ...doc.data() });
				});
				cats.sort((a, b) => (a.priority || 0) - (b.priority || 0));
				setCategories(cats);
			} catch (err) {
				console.error('Failed to fetch categories', err);
			}
		};
		fetchCategories();
	}, []);

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		if (name === 'pax') {
			const newPax = Number(value);
			setForm((prev) => {
				// Always keep redeem_amt in sync with pax by default.
				if (!customToken) {
					return { ...prev, pax: newPax, token: newPax * 3, redeem_amt: newPax };
				}
				return { ...prev, pax: newPax, redeem_amt: newPax };
			});
		} else if (name === 'token') {
			setCustomToken(true);
			setForm((prev) => ({ ...prev, token: Number(value) }));
		} else {
			setForm((prev) => ({
				...prev,
				[name]: type === 'checkbox' ? checked : value,
			}));
		}
	};

	const handleDialogOpen = (guest = null) => {
		if (guest) {
			setForm({ ...guest, redeem_amt: guest.redeem_amt ?? (Number(guest.pax) || 0) });
			setIsEditing(true);
			setCustomToken(false); // Allow auto-calc unless user edits token
		} else {
			setForm({
				id: '',
				name: '',
				category: '',
				pax: 1,
				token: 3,
				redeem_amt: 1,
				value: 0,
				table_no: 0,
				is_redeemed: false,
				is_arrived: false,
				is_confirmed: false,
			});
			setIsEditing(false);
			setCustomToken(false);
		}
		setDialogOpen(true);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
		setIsEditing(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			if (isEditing) {
				const guestRef = doc(db, 'guests', form.id);
				await updateDoc(guestRef, {
					name: form.name,
					category: form.category,
					pax: Number(form.pax),
					token: Number(form.token),
					value: Number(form.value),
					redeem_amt: Number(form.redeem_amt) || Number(form.pax) || 0,
					table_no: Number(form.table_no),
					is_redeemed: form.is_redeemed,
					is_arrived: form.is_arrived,
					is_confirmed: form.is_confirmed,
				});
			} else {
				await addDoc(collection(db, 'guests'), {
					name: form.name,
					category: form.category,
					pax: Number(form.pax),
					token: Number(form.token),
					value: Number(form.value),
					redeem_amt: Number(form.redeem_amt) || Number(form.pax) || 0,
					table_no: Number(form.table_no),
					is_redeemed: form.is_redeemed,
					is_arrived: form.is_arrived,
					is_confirmed: form.is_confirmed,
				});
			}
			// Refresh list
			const querySnapshot = await getDocs(collection(db, 'guests'));
			const guestsArr = [];
			querySnapshot.forEach((doc) => {
				guestsArr.push({ id: doc.id, ...doc.data() });
			});
			setGuests(guestsArr);
			handleDialogClose();
		} catch (err) {
			console.error('Failed to submit guest form', err);
			// optionally show a user-friendly message here
		}
	};

	const handleSort = (key) => {
		setSortConfig((prev) => {
			if (prev.key === key) {
				return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
			}
			return { key, direction: 'asc' };
		});
	};

	const handleCopy = async (text) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopySuccess(text);
			setTimeout(() => setCopySuccess(''), 1200);
		} catch (err) {
			setCopySuccess('Error');
			setTimeout(() => setCopySuccess(''), 1200);
		}
	};

	const handleRefresh = async () => {
		setLoading(true);
		try {
			const querySnapshot = await getDocs(collection(db, 'guests'));
			const guestsArr = [];
			querySnapshot.forEach((doc) => {
				guestsArr.push({ id: doc.id, ...doc.data() });
			});
			setGuests(guestsArr);
		} catch (err) {
			console.error('Failed to refresh guests', err);
		} finally {
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		const auth = getAuth();
		await signOut(auth);
		window.location.reload();
	};

	const filteredAndSortedGuests = React.useMemo(
		() => {
			let filtered = [...guests];
			if (searchName) {
				filtered = filtered.filter((g) =>
					g.name && g.name.toLowerCase().includes(searchName.toLowerCase())
				);
			}
			if (searchCategory) {
				filtered = filtered.filter((g) => g.category === searchCategory);
			}
			if (filterArrived) filtered = filtered.filter((g) => g.is_arrived);
			if (filterConfirmed) filtered = filtered.filter((g) => g.is_confirmed);
			if (showUnconfirmed) filtered = filtered.filter((g) => !g.is_confirmed);
			if (showUnarrived) filtered = filtered.filter((g) => !g.is_arrived);
			if (!sortConfig.key) return filtered;
			return filtered.sort((a, b) => {
				let aVal = a[sortConfig.key];
				let bVal = b[sortConfig.key];
				if (typeof aVal === 'string') aVal = aVal.toLowerCase();
				if (typeof bVal === 'string') bVal = bVal.toLowerCase();
				if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
				if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
				return 0;
			});
		},
		[
			guests,
			sortConfig,
			filterArrived,
			filterConfirmed,
			searchName,
			searchCategory,
			showUnconfirmed,
			showUnarrived,
		]
	);

	// Calculate totals for summary row
	const totalNames = filteredAndSortedGuests.length;
	const totalPax = filteredAndSortedGuests.reduce((sum, g) => sum + (Number(g.pax) || 0), 0);
	const totalToken = filteredAndSortedGuests.reduce((sum, g) => sum + (Number(g.token) || 0), 0);
	const totalValue = filteredAndSortedGuests.reduce((sum, g) => sum + (Number(g.value) || 0), 0);
	const totalRedeem = filteredAndSortedGuests.reduce((sum, g) => sum + (Number(g.redeem_amt) || 0), 0);

	const handleDelete = async () => {
		if (!form.id) return;
		try {
			await deleteDoc(doc(db, 'guests', form.id));
			// Refresh list
			const querySnapshot = await getDocs(collection(db, 'guests'));
			const guestsArr = [];
			querySnapshot.forEach((doc) => {
				guestsArr.push({ id: doc.id, ...doc.data() });
			});
			setGuests(guestsArr);
			setDeleteConfirmOpen(false);
			handleDialogClose();
		} catch (err) {
			console.error('Failed to delete guest', err);
		}
	};

	return (
		<Box p={3}>
			{/* Summary Row Above Table Title */}
			<Box display="flex" gap={2} mb={2} flexDirection={isMobile ? 'column' : 'row'} justifyContent="center" alignItems="center">
				<Box display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'} alignItems="center">
					<Typography variant="subtitle1" align={isMobile ? 'center' : 'left'}>Total Names: {totalNames}</Typography>
					<Typography variant="subtitle1" align={isMobile ? 'center' : 'left'}>Total Pax: {totalPax}</Typography>
					<Typography variant="subtitle1" align={isMobile ? 'center' : 'left'}>Total Token: {totalToken}</Typography>
					<Typography variant="subtitle1" align={isMobile ? 'center' : 'left'}>Total Redeem: {totalRedeem}</Typography>
					<Typography variant="subtitle1" align={isMobile ? 'center' : 'left'}>Total Value: {totalValue}</Typography>
				</Box>
			</Box>
			<Box
				display="flex"
				flexDirection={isMobile ? 'column' : 'row'}
				justifyContent="space-between"
				alignItems={isMobile ? 'stretch' : 'center'}
				mb={2}
				gap={2}
			>
				<Typography variant="h5" fontWeight={600}>
					Guest List
				</Typography>
				<Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
					<TextField
						placeholder="Search name..."
						value={searchName}
						onChange={(e) => setSearchName(e.target.value)}
						size="small"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						sx={{ minWidth: 180 }}
					/>
					<TextField
						select
						value={searchCategory}
						onChange={(e) => setSearchCategory(e.target.value)}
						size="small"
						displayEmpty
						sx={{ minWidth: 140 }}
					>
						<MenuItem value="">All Categories</MenuItem>
						{categories.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.value}
							</MenuItem>
						))}
					</TextField>
					<FormControlLabel
						control={
							<Checkbox
								checked={filterArrived}
								onChange={(e) =>
									setFilterArrived(e.target.checked)
								}
							/>
						}
						label="Show Arrived"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={filterConfirmed}
								onChange={(e) =>
									setFilterConfirmed(e.target.checked)
								}
							/>
						}
						label="Show Confirmed"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={showUnarrived}
								onChange={(e) =>
									setShowUnarrived(e.target.checked)
								}
							/>
						}
						label="Show Unarrived"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={showUnconfirmed}
								onChange={(e) =>
									setShowUnconfirmed(e.target.checked)
								}
							/>
						}
						label="Show Unconfirmed"
					/>
					<Box display="flex" gap={1} flexDirection={isMobile ? 'column' : 'row'}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => handleDialogOpen()}
							fullWidth={isMobile}
						>
							Add Guest
						</Button>
						<Button
							variant="outlined"
							color="primary"
							startIcon={<QrCodeScannerIcon />}
							onClick={() => navigate('/qr')}
							fullWidth={isMobile}
						>
							Scan QR
						</Button>
						<Button
							variant="outlined"
							color="info"
							onClick={handleRefresh}
							startIcon={<RefreshIcon />}
							fullWidth={isMobile}
						>
							Refresh
						</Button>
						<Button
							variant="outlined"
							color="secondary"
							onClick={handleLogout}
							sx={{ ml: isMobile ? 0 : 2 }}
							fullWidth={isMobile}
						>
							Logout
						</Button>
					</Box>
				</Box>
			</Box>
			{loading ? (
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					minHeight={200}
				>
					<CircularProgress />
				</Box>
			) : (
				<TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>ID</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === 'name'}
										direction={
											sortConfig.key === 'name'
												? sortConfig.direction
												: 'asc'
										}
										onClick={() => handleSort('name')}
									>
										Name
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === 'category'}
										direction={
											sortConfig.key === 'category'
												? sortConfig.direction
												: 'asc'
										}
										onClick={() => handleSort('category')}
									>
										Category
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === 'pax'}
										direction={
											sortConfig.key === 'pax'
												? sortConfig.direction
												: 'asc'
										}
										onClick={() => handleSort('pax')}
									>
										Pax
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === 'token'}
										direction={
											sortConfig.key === 'token'
												? sortConfig.direction
												: 'asc'
										}
										onClick={() => handleSort('token')}
									>
										Token
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === 'redeem_amt'}
										direction={
												sortConfig.key === 'redeem_amt'
													? sortConfig.direction
													: 'asc'
										}
										onClick={() => handleSort('redeem_amt')}
									>
										Redeem
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === 'table_no'}
										direction={
											sortConfig.key === 'table_no'
												? sortConfig.direction
												: 'asc'
										}
										onClick={() => handleSort('table_no')}
									>
										Table No
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === 'is_arrived'}
										direction={
											sortConfig.key === 'is_arrived'
												? sortConfig.direction
												: 'asc'
										}
										onClick={() => handleSort('is_arrived')}
									>
										Arrived
									</TableSortLabel>
								</TableCell>
								<TableCell>
									<TableSortLabel
										active={sortConfig.key === 'is_confirmed'}
										direction={
											sortConfig.key === 'is_confirmed'
												? sortConfig.direction
												: 'asc'
										}
										onClick={() => handleSort('is_confirmed')}
									>
										Confirmed
									</TableSortLabel>
								</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredAndSortedGuests.map((guest) => (
								<TableRow key={guest.id}
									sx={{
										backgroundColor:
											guest.is_confirmed ? '#e6ffe6' : guest.is_arrived ? '#e6f3ff' : 'inherit',
									}}
								>
									<TableCell>
										<Box display="flex" alignItems="center">
											<Tooltip title={copySuccess === guest.id ? 'Copied!' : 'Copy Link'}>
												<IconButton size="small" onClick={() => handleCopy(`https://joejennie.com/?id=${guest.id}`)}>
													<ContentCopyIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Typography variant="body2" sx={{ ml: 1 }}>{guest.id}</Typography>
										</Box>
									</TableCell>
									<TableCell>{guest.name}</TableCell>
									<TableCell>{guest.category}</TableCell>
									<TableCell>{guest.pax}</TableCell>
									<TableCell>{guest.token}</TableCell>
									<TableCell>{guest.redeem_amt}</TableCell>
									<TableCell>{guest.table_no}</TableCell>
									<TableCell>{guest.is_arrived ? 'Yes' : 'No'}</TableCell>
									<TableCell>{guest.is_confirmed ? 'Yes' : 'No'}</TableCell>
									<TableCell>
										<Button
											size="small"
											variant="outlined"
											onClick={() => handleDialogOpen(guest)}
										>
											Edit
										</Button>
										<Tooltip title="Open QR Scanner">
												<IconButton
													size="small"
													color="secondary"
													onClick={() => navigate(`/qr?id=${guest.id}`)}
													sx={{ ml: 1 }}
												>
												<CheckIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
			<Dialog
				open={dialogOpen}
				onClose={handleDialogClose}
				maxWidth="xs"
				fullWidth
				fullScreen={isMobile}
			>
				<DialogTitle>{isEditing ? 'Edit Guest' : 'Add Guest'}</DialogTitle>
				<form onSubmit={handleSubmit}>
					<DialogContent>
						<TextField
							label="Name"
							name="name"
							value={form.name}
							onChange={handleChange}
							fullWidth
							required
							margin="normal"
						/>
						<TextField
							select
							label="Category"
							name="category"
							value={form.category}
							onChange={handleChange}
							fullWidth
							required
							margin="normal"
						>
							{categories.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.value}
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="Pax"
							name="pax"
							type="number"
							value={form.pax}
							onChange={handleChange}
							fullWidth
							margin="normal"
							inputProps={{ min: 1 }}
						/>
						<TextField
							label="Token"
							name="token"
							type="number"
							value={form.token}
							onChange={handleChange}
							fullWidth
							margin="normal"
						/>
						<TextField
							label="Value"
							name="value"
							type="number"
							value={form.value}
							onChange={handleChange}
							fullWidth
							margin="normal"
						/>
						<TextField
							label="Redeem Amt"
							name="redeem_amt"
							type="number"
							value={form.redeem_amt}
							onChange={handleChange}
							fullWidth
							margin="normal"
							inputProps={{ min: 0 }}
						/>
						<TextField
							label="Table No"
							name="table_no"
							type="number"
							value={form.table_no}
							onChange={handleChange}
							fullWidth
							margin="normal"
						/>
						<Box display="flex" gap={2} mt={2}>
							<Button
								variant={form.is_arrived ? 'contained' : 'outlined'}
								color="success"
								onClick={() =>
									setForm((f) => ({ ...f, is_arrived: !f.is_arrived }))
								}
								fullWidth
							>
								Arrived: {form.is_arrived ? 'Yes' : 'No'}
							</Button>
							{/* Redeemed button removed per request; redeem amount is shown above */}
							<Button
								variant={form.is_confirmed ? 'contained' : 'outlined'}
								color="info"
								onClick={() =>
									setForm((f) => ({ ...f, is_confirmed: !f.is_confirmed }))
								}
								fullWidth
							>
								Confirmed: {form.is_confirmed ? 'Yes' : 'No'}
							</Button>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleDialogClose}>Cancel</Button>
						{isEditing && (
							<Button
								color="error"
								onClick={() => setDeleteConfirmOpen(true)}
								sx={{ mr: 'auto' }}
							>
								Delete
							</Button>
						)}
						<Button
							type="submit"
							variant="contained"
							color="primary"
						>
							{isEditing ? 'Update' : 'Add'}
						</Button>
					</DialogActions>
				</form>
			</Dialog>
			<Dialog
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Delete Guest?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete this guest? This action cannot be
						undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
					<Button
						onClick={handleDelete}
						color="error"
						variant="contained"
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default GuestList;
